import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { FormModal, Field, FilePicker, inputCls, btnPrimary, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { EscStatusSelect } from './WorkflowEscNew';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import { formatDateTime } from '../utils/dateFormat';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { EngagementName } from '../components/workflow/CoverageDisplay';
import { engagementDisplayName } from '../utils/engagementName';

// Engagement Status Change — detail page (docs/modules/workflow-esc.md).
// Single-sided keyed review (workflow:esc:review); approval writes
// engagements.status and locks the request.

// Named rather than inline: History and the "Changes since it was returned"
// card must name a field identically. `reviewerName` stays so stored history
// diffs that carry that key still render with a label.
const ESC_CHANGE_LABELS = {
  status: 'Engagement Status',
  requestedStatus: 'Requested Status',
  reason: 'Reason',
  reviewerName: 'Reviewer',
};

const WorkflowEscDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [engagementLock, setEngagementLock] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/esc/${id}`),
        fetchJSON(`/api/workflow/esc/${id}/events`),
      ]);
      setRequest(detail.request);
      setCanReview(detail.canReview);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setEngagementLock(detail.engagementLock || null);
      setEvents(hist.events || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    setActing(true); setError(null);
    try {
      await fetchJSON(`/api/workflow/esc/${id}/${action}`, { method: 'POST' });
      setNotice(action === 'approve'
        ? 'Approved — the new status is written on the engagement.'
        : 'Re-submitted — reviewers have been notified.');
      await load();
    } catch (e) { setError(e.message); } finally { setActing(false); }
  };

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (canReview);
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: ESC_CHANGE_LABELS,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/esc"
      backLabel="Engagement Status Change"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="Engagement Status Change"
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            showDecider
            lock={engagementLock}
            sides={[{
              key: 'main', label: null, status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onApprove={canReview ? () => act('approve') : null}
            approveLabel={() => 'Approve & Apply'}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify ? () => setModifying(true) : null}
            onResubmit={canModify && request.status === 'awaiting_info' ? () => act('resubmit') : null}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
          />

          <ChangesSinceReturnCard
            events={events}
            changeLabels={ESC_CHANGE_LABELS}
          />

          <SectionCard title="Requested Change">
            <DetailGrid>
              <DetailItem label="Engagement"><EngagementName name={request.engagementName} now={request.engagementNameNow} serviceDescription={request.serviceDescription} /></DetailItem>
              <DetailItem label="Status at Submission">{request.currentStatus}</DetailItem>
              <DetailItem label="Requested Status" field="requestedStatus">
                <span className="font-medium text-slate-900">{request.requestedStatus}</span>
              </DetailItem>
              <DetailItem label="Reason" field="reason">{request.reason}</DetailItem>
            </DetailGrid>
          </SectionCard>

          <SectionCard title="Attachments">
            <AttachmentsList
              files={request.files || []}
              downloadHref={(f) => `${API_BASE_URL}/api/workflow/esc/${request.id}/files/${f.id}`}
            />
            <FieldCommentAnchor field="files" label="Attachments" />
          </SectionCard>

          <HistoryCard
            events={events}
            changeLabels={ESC_CHANGE_LABELS}
          />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/workflow/esc/${request.id}/reject`, {
                  method: 'POST', body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <EscModifyModal
              request={request}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(request.status === 'awaiting_info'
                  ? 'Changes saved. Press Re-submit to send the request back for review.'
                  : 'Changes saved.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the request and its files. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => { await fetchJSON(`/api/workflow/esc/${request.id}`, { method: 'DELETE' }); navigate('/workflow/esc'); }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

// The engagement is deliberately NOT editable here — a different engagement is
// a different request. Only the target status, the reason and the attachments.
const EscModifyModal = ({ request, events = [], onClose, onDone }) => {
  const [statuses, setStatuses] = useState([]);
  const [requestedStatus, setRequestedStatus] = useState(request.requestedStatus || '');
  const [reason, setReason] = useState(request.reason || '');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { removeIds, toggle: toggleRemove, appendTo } = useRemovableFiles();

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/esc/options');
        setStatuses(data.statuses || []);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  // Everything still empty, keyed to the control it belongs to — after the
  // first Save the modal marks those controls and lists them in the banner.
  const gaps = useMemo(() => {
    const g = [];
    if (!requestedStatus) g.push({ key: 'requestedStatus', label: 'New Status' });
    if (!reason.trim()) g.push({ key: 'reason', label: 'Reason' });
    return g;
  }, [requestedStatus, reason]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true); setError(null);
    try {
      const form = new FormData();
      form.set('requestedStatus', requestedStatus);
      form.set('reason', reason.trim());
      appendTo(form);
      files.forEach((f) => form.append('files', f));
      await fetchJSON(`/api/workflow/esc/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className={btnPrimary}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <ReturnCommentsBanner events={events} />
      <FormValidationProvider value={validation.providerValue}>
        <Field label="Engagement">
          <p className="text-sm text-slate-700">{engagementDisplayName(request.engagementName, request.serviceDescription)}</p>
          <p className="text-xs text-slate-500 mt-1">A different engagement means a different request — submit a new one.</p>
        </Field>
        <Field label="New Status" field="requestedStatus" required>
          <EscStatusSelect
            statuses={statuses}
            currentStatus={request.currentStatus}
            value={requestedStatus}
            onChange={(e) => setRequestedStatus(e.target.value)}
          />
        </Field>
        <Field label="Reason" field="reason" required>
          <textarea rows="3" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
        </Field>
        <EditableAttachments
          files={request.files || []}
          removeIds={removeIds}
          onToggle={toggleRemove}
          downloadHref={(f) => `${API_BASE_URL}/api/workflow/esc/${request.id}/files/${f.id}`}
        />
        <FieldCommentInline field="files" />
        <Field label="Add Supporting Files">
          <FilePicker multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            onChange={(e) => setFiles(Array.from(e.target.files || []))} />
        </Field>
      </FormValidationProvider>
      {(error || validation.error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error || validation.error}</span>
        </div>
      )}
    </FormModal>
  );
};

export default WorkflowEscDetail;
